import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import ApplicationLogo from '../components/ApplicationLogo'
import DynamicForm, { IDynamicForm, IDynamicFormElement, IRequiredField } from '../components/Form/DynamicForm'
import { useDispatch } from 'react-redux'
import { addNewForm, createOrUpdateElementData, updateFormLoading } from '../components/Form/formsSlice'
import GenerateForm from '../components/Form/GenerateForm'
import { LoadingButton } from '@mui/lab'
import { IAxiosDefaultDataObject } from '../config'
import { verifyStrongPassword } from '../features/user/UsersApp'
import UserService from '../services/user'
import { updateQuickNotificationDetails } from '../features/notifications/notificationsSlice'
import { handleRequestError } from '../services/api'

export default function UnblockAccount() {

    const dispatch = useDispatch()
    const navigate = useNavigate()
    const params = useParams()

    const [loading, setLoading] = useState<boolean>(false)
    const [token, setToken] = useState<string>(params.token ? params.token : "")

    const [isSuccess, setIsSuccess] = useState<boolean>(false)

    const elemPrefix: string = "request-unblock-account_"

    const setPasswordElements: IDynamicFormElement[] = [
        {
            id: elemPrefix + "email",
            xs: 12,
            type: "TextField",
            label: "Email",
            fullWidth: true
        },
        {
            id: elemPrefix + "password",
            xs: 12,
            type: "TextField",
            label: "Password",
            fullWidth: true,
            fieldType: "password",
            enableStrongPassword: true
        }
    ]

    const requestNewPasswordSetElements: IDynamicFormElement[] = [
        {
            id: elemPrefix + "email",
            xs: 12,
            type: "TextField",
            label: "Email",
            fullWidth: true
        }
    ]

    const form: IDynamicForm = {
        name: "reset-password-form",
        elements: token ? setPasswordElements : requestNewPasswordSetElements
    }

    const elementsData = DynamicForm.useReturnAllFormsData()
    const singleElemDataValue = (name: string) => DynamicForm.returnSingleFormElementData(elemPrefix + name, elementsData).value

    /**
     * Update loading
     */
    const updateLoading = (status: boolean) => {
        dispatch(updateFormLoading({ formName: form.name, loading: status }))
        setLoading(status)
    }

    /**
     * Create form elem data
     */
    const createFormElemData = () => {
        form.elements.map((elem) => dispatch(createOrUpdateElementData({
            name: elem.id,
            value: ""
        })))
    }

    /**
     * Reset form
     */
    const resetForm = () => {
        createFormElemData()
        DynamicForm.createOrUpdateFormMessage(form.name, { shown: false, type: "success", message: "" }, dispatch)
    }


    /**
     * Handle submit
     */
    const handleSubmit = () => {

        let data: IAxiosDefaultDataObject = {}

        data.email = singleElemDataValue("email")

        if (token){
            data.password = singleElemDataValue("password")
            data.password_set_unique_id = token
        }

        const isPasswordStrong = (password: string) => {
            const verify = verifyStrongPassword(password)

            if (!verify.lowercase){
                return false
            }

            if (!verify.uppercase){
                return false
            }

            if (!verify.numbers){
                return false
            }

            if (!verify.special_character){
                return false
            }

            if (!verify.length){
                return false
            }

            if (!verify.valid){
                return false
            }

            return true
        }

        let requiredFields: IRequiredField[] = [
            {
                id: elemPrefix + "email",
                message: "Email is required"
            },
        ]

        if (token){
            requiredFields.push({
                id: elemPrefix + "password",
                message: !isPasswordStrong(data.password) ? "Password is not strong enough!" : "Password is required",
                customValidate: (val) => {

                    if (!val){
                        return false
                    }

                    if (!isPasswordStrong(val)){
                        return false
                    }

                    return true
                },
            },)
        }

        DynamicForm.resetRequiredFieldsHelperText(requiredFields, elementsData, dispatch)
        const verifyForm = DynamicForm.verifyForm(elementsData, requiredFields)

        if (verifyForm.isValid){
            DynamicForm.updateFormLoadingStatus(form.name, true, dispatch)

            let func = UserService.resetPassword(data.email, data.password, data.password_set_unique_id, true)

            if (!token){
                func = UserService.requestForgotPassword(data.email, true)
            }

            func
                .then(response => {

                    const { data } = response

                    console.log("data", data)

                    const message = data.message ? data.message : "Success!"
                    DynamicForm.updateFormLoadingStatus(form.name, false, dispatch)
                    DynamicForm.createOrUpdateFormMessage(form.name, { shown: true, type: "success", message: message }, dispatch)

                    updateQuickNotificationDetails(true, message, "success", dispatch)

                    resetForm()

                    setTimeout(() => navigate("/sign-in"), 1500)
                    setIsSuccess(true)

                })
                .catch(error => {

                    console.log(error)
                    const message = error.response.data.message ? error.response.data.message : "Fatal Error"

                    setIsSuccess(false)
                    DynamicForm.updateFormLoadingStatus(form.name, false, dispatch)
                    DynamicForm.createOrUpdateFormMessage(form.name, { shown: true, type: "error", message: message }, dispatch)
                    handleRequestError(error, dispatch)

                })

        }else{
            DynamicForm.updateRequiredFieldsHelperText(verifyForm.fields, elementsData, dispatch)
        }

    }

    useEffect(() => {
        dispatch(addNewForm(form))
        createFormElemData()
    }, [])

    useEffect(() => {
        if (params.token){
            setToken(params.token)
        }
    }, [params])

    return(
        <div className="login-page-container">
            <div className="content-of-login-page-container">

                <div className="login-form-box-container">
                    <div className="content-of-login-form-box-container">

                        <div className="logo-box-of-login-form-box-container">
                            <div className="content-of-logo-box-of-login-form-box-container">
                                <ApplicationLogo />
                            </div>
                        </div>

                        <div className="text-box-of-login-form-box-container">
                            <div className="content-of-text-box-of-login-form-box-container">
                                <h2>Unblock account</h2>
                                {!token ? <p>Request to unblock your account</p> : <p>Create a new password to unblock your account <a href="/unblock-account">Request a new link</a></p>}
                            </div>
                        </div>

                        <div className="form-of-login-form-box-container">
                            <div className="content-of-form-of-login-form-box-container">
                                <GenerateForm name={form.name} />

                                <div className="submit-button-box-of-login-form">
                                    <LoadingButton onClick={() => handleSubmit()} disabled={isSuccess} className="general-button" loading={loading} variant="contained" fullWidth type="submit">{!token ? "Request Link" : "Unblock Account"}</LoadingButton>
                                </div>
                            </div>
                        </div>


                    </div>
                </div>

            </div>
        </div>
    )
}